// ─── ErrorFallback ───
// Full-screen recovery UI shown when an uncaught render/route error is caught by
// ErrorBoundary (outer backstop) or the router's defaultErrorComponent (route
// errors). Presentational only — the caller supplies the reset behaviour. Style
// mirrors ListView's error card (AlertCircle + danger token + surface-raised
// buttons). The error MESSAGE is shown only in DEV (mirrors the backend's
// handleError, which hides raw detail in production).

import { AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ErrorFallbackProps {
  /** The thrown error — its message is surfaced in dev only. */
  error?: unknown;
  /** When provided, renders a primary "Retry" that re-attempts in place (route
   *  reset / boundary clear) without a full reload. */
  onRetry?: () => void;
}

const buttonBase =
  'coarse:min-h-[44px] rounded-md px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]';

export function ErrorFallback({ error, onRetry }: ErrorFallbackProps): React.JSX.Element {
  const { t } = useTranslation('common');
  const message = error instanceof Error ? error.message : undefined;

  return (
    <div
      role="alert"
      className="flex min-h-[50vh] w-full flex-col items-center justify-center gap-3 px-6 py-12 text-center"
    >
      <AlertCircle className="h-10 w-10 text-[var(--color-danger)]" aria-hidden="true" />
      <h1 className="text-lg font-semibold text-[var(--text-primary)]">
        {t('errorBoundary.title')}
      </h1>
      <p className="max-w-md text-sm text-[var(--text-secondary)]">
        {t('errorBoundary.description')}
      </p>
      {import.meta.env.DEV && message && (
        <pre className="mt-1 max-w-md overflow-x-auto rounded-md bg-surface-overlay px-3 py-2 text-left text-xs text-[var(--text-tertiary)]">
          {message}
        </pre>
      )}
      <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
        {/* 🚨 GHE #447 — this CTA deliberately stays hand-rolled, and the reason
            is a measurement, not an oversight. `main.tsx` imports this module
            DIRECTLY (it backs both `ErrorBoundary` and the router's
            `defaultErrorComponent`), so it is eager and lands in the entry
            chunk. Adopting `Button` here drags `cn` → `tailwind-merge` into
            first paint: measured +9.0 KB gz via a deep import, +14.7 KB via the
            barrel, against an initial-JS ceiling that had 8.4 KB of headroom.
            That is a large permanent cost on every page load for one button on
            a screen users should rarely reach. Pinned in
            `styles/button-adoption.invariant.test.ts`. */}
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className={`${buttonBase} bg-primary-600 text-[var(--text-inverse)] hover:bg-primary-700`}
          >
            {t('actions.retry')}
          </button>
        )}
        <button
          type="button"
          onClick={() => window.location.reload()}
          className={`${buttonBase} border border-border bg-surface-raised text-[var(--text-secondary)] hover:border-border-strong hover:bg-surface-overlay hover:text-[var(--text-primary)]`}
        >
          {t('errorBoundary.reload')}
        </button>
        <button
          type="button"
          // Hard navigation (not the router) — the router itself may be the thing
          // that threw, so we can't rely on it to recover.
          onClick={() => {
            window.location.href = '/app';
          }}
          className={`${buttonBase} border border-border bg-surface-raised text-[var(--text-secondary)] hover:border-border-strong hover:bg-surface-overlay hover:text-[var(--text-primary)]`}
        >
          {t('errorBoundary.goHome')}
        </button>
      </div>
    </div>
  );
}
